import React from 'react'

export interface UseCssSizeVarOptions {
  onChange?: (size: number) => void
  defaultValue?: number
}

const getRootElement = () =>
  document.getElementById('rpa-root') ??
  document.getElementById('rpa-app') ??
  document.documentElement

export function useCssSizeVar<T extends HTMLElement>(
  ref: React.RefObject<T>,
  varName: string,
  options?: UseCssSizeVarOptions,
) {
  const { onChange, defaultValue } = options ?? {}

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    const el = ref.current
    const root = getRootElement()

    if (!root) {
      return
    }

    const write = () => {
      const size = el ? Math.round(el.getBoundingClientRect().height) : defaultValue ?? 0
      root.style.setProperty(varName, `${size}px`)
      onChange?.(size)
    }

    write()

    if (!el) {
      return () => {
        if (typeof defaultValue === 'number') {
          root.style.setProperty(varName, `${Math.round(defaultValue)}px`)
        }
      }
    }

    let resizeObserver: ResizeObserver | null = null

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(write)
      resizeObserver.observe(el)
    } else {
      window.addEventListener('resize', write)
    }

    const mutationObserver = new MutationObserver(write)
    mutationObserver.observe(el, { subtree: true, childList: true, attributes: true })

    return () => {
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
      if (!resizeObserver) {
        window.removeEventListener('resize', write)
      }
    }
  }, [ref, varName, onChange, defaultValue])
}
